// @flow

import React from "react"
import { compose } from "react-apollo"
import { FormattedMessage, injectIntl } from "react-intl"
import { TweenMax, Cubic } from "gsap"
import SVGLoader from "../../UI/SVGLoader"
import BackgroundContainer from "../../UI/BackgroundContainer"
import background from "./background.jpg"
import Title from "../Shared/Title"
import TitleIcon from "../Shared/TitleIcon"
import TitleContent from "../Shared/TitleContent"
import Button from "../Shared/Button"
import CTA from "../Shared/CTA"
import ForecastMetric from "./ForecastMetric"
import { withCurrentKioskData, withCAMMForecast } from "../queries"
import { levelForValue } from "../Shared/AQILegend"
import AlertBar from "./AlertBar"

import {
  ButtonGroup,
  Intro,
  LocalForecast,
  LocalForecastMetrics,
  LocalHeadline,
  LocalSubHeadline,
  InfoPanel,
  CountyHeadline,
  CountyDescription,
  CountyForecast,
  Flag,
  ForecastPanel,
  LocationName,
  Restrictions
} from "./UI"

import { flagForStatus } from "../Flags"

import Restriction from "./Restriction"

import iconForecast from "../Icons/iconForecast.svg"

import type { RouterHistory } from "react-router-dom"
import type { IntlShape } from "react-intl"
import type { CurrentKiosk, CAMMForecast } from "../queries"
import type { Metric } from "../Shared/AQILegend"

type Props = {
  intl: IntlShape,
  history: RouterHistory,
  data: CurrentKiosk,
  camm: CAMMForecast
}

class CurrentAirQualityView extends React.Component<Props> {
  forecastPanel: ?HTMLElement
  countyForecastPanel: ?HTMLElement
  introText: Array<HTMLElement>
  panelContents: Array<HTMLElement>

  constructor(props) {
    super(props)
    this.forecastPanel = null
    this.introText = []
    this.panelContents = []
  }

  componentDidMount = () => {
    const { data: { loading } } = this.props
    TweenMax.set(this.forecastPanel, { y: "+=100", opacity: 0 })
    TweenMax.set(this.countyForecastPanel, { x: "-=60", opacity: 0 })
    TweenMax.set(this.introText, { y: "-=40", opacity: 0 })
    TweenMax.set(this.panelContents, { y: "+=20", opacity: 0 })
    this.showText()
    if (!loading) {
      setTimeout(() => this.showForecast(), 500)
    }
  }

  componentDidUpdate = ({ data: { loading } }) => {
    const { data } = this.props
    if (loading !== data.loading && !data.loading) {
      setTimeout(() => this.showForecast(), 500)
    }
  }

  showText = () =>
    TweenMax.staggerTo(
      this.introText,
      0.5,
      {
        y: "0",
        opacity: 1,
        ease: Cubic.easeOut
      },
      0.25
    )

  showForecast = () => {
    TweenMax.to(this.forecastPanel, 0.5, {
      y: "0",
      opacity: 1,
      ease: Cubic.easeOut,
      onComplete: () => {
        TweenMax.to(this.countyForecastPanel, 1.5, {
          x: "0",
          opacity: 1,
          delay: 0.25,
          ease: Cubic.easeOut
        })

        TweenMax.staggerTo(
          this.panelContents.filter(el => el !== null),
          0.5,
          {
            y: "0",
            opacity: 1,
            ease: Cubic.easeOut
          },
          0.25
        )
      }
    })
  }

  handleHistoryClick = () => {
    const { history } = this.props
    const path = {
      pathname: "/kiosk/history",
      state: {
        modal: true
      }
    }
    history.push(path)
  }

  handleMetricClick = (metric: Metric) => {
    const { history } = this.props
    const path = {
      pathname: `/kiosk/glossary/${metric}`,
      state: {
        modal: true
      }
    }
    history.push(path)
  }

  handleLocationsClick = () => {
    const { history } = this.props
    const path = {
      pathname: "/kiosk/locations",
      state: {
        modal: true
      }
    }
    history.push(path)
  }

  renderCAMMForecast = () => {
    const { camm } = this.props
    const forecast = (!camm.loading && camm.currentForecast) || {}
    const restrictions = forecast.restrictions || {}
    return (
      <InfoPanel innerRef={el => (this.countyForecastPanel = el)}>
        <CountyHeadline>
          <FormattedMessage id="kiosk.forecast.countyTitle" />
        </CountyHeadline>
        <AlertBar data={camm} />
        <CountyForecast>
          <Flag>
            <SVGLoader
              height="100%"
              width="100%"
              path={flagForStatus(forecast.status)}
            />
          </Flag>
          <CountyDescription>
            {forecast.status ? (
              <FormattedMessage
                id={`kiosk.forecast.description.${forecast.status}`}
              />
            ) : (
              ""
            )}
          </CountyDescription>
        </CountyForecast>
        <Restrictions>
          <Restriction
            restriction="woodburning"
            allowed={!restrictions.woodburning}
          />
          <Restriction
            restriction="leafblower"
            allowed={!restrictions.leafBlowers}
          />
          <Restriction
            restriction="atv"
            allowed={!restrictions.offRoadVehicles}
          />
        </Restrictions>
      </InfoPanel>
    )
  }

  renderLocalForecast = () => {
    const { data } = this.props
    const currentKiosk = data.currentKiosk || {}
    return (
      <LocalForecastMetrics innerRef={el => this.panelContents.push(el)}>
        {!data.loading && currentKiosk.readings ? (
          ["pm2_5", "pm10", "o3"].map((metric, index) => {
            const readings = [...currentKiosk.readings.live]
              .map(reading => reading[metric])
              .map(reading => Math.round(reading))
            const readingsWithData = readings.filter(v => v >= 0)
            const lastReading =
              readingsWithData.length > 0
                ? readingsWithData[readingsWithData.length - 1]
                : 0
            const diff =
              readingsWithData.length > 0
                ? readingsWithData[0] - lastReading
                : 0
            const direction =
              diff === 0 ? "stable" : diff > 0 ? "increasing" : "decreasing"
            const level = levelForValue(readingsWithData[0] || 0)
            return (
              <ForecastMetric
                key={`${metric}_${index}`}
                level={level}
                direction={direction}
                pollutant={metric}
                delay={1.75 + index * 0.1}
                readings={readings}
                onClick={this.handleMetricClick}
              />
            )
          })
        ) : (
          <span />
        )}
      </LocalForecastMetrics>
    )
  }

  render() {
    const { intl: { formatMessage }, data } = this.props
    const currentKiosk = data.currentKiosk || {}
    return (
      <BackgroundContainer imgSrc={background} flex={true}>
        <Intro>
          <TitleIcon>
            <SVGLoader
              path={iconForecast}
              height="100%"
              width="100%"
              fill="#fff"
            />
          </TitleIcon>
          <div>
            <Title innerRef={el => this.introText.push(el)}>
              <FormattedMessage id="kiosk.forecast.title" />
            </Title>
            <TitleContent innerRef={el => this.introText.push(el)}>
              {formatMessage({ id: "kiosk.forecast.intro" }).replace(
                "[SHORT NAME]",
                data.loading ? "" : currentKiosk.shortName
              )}
            </TitleContent>
            <TitleContent innerRef={el => this.introText.push(el)}>
              {formatMessage({ id: "kiosk.forecast.intro2" }).replace(
                "[SHORT NAME]",
                data.loading ? "" : currentKiosk.shortName
              )}
            </TitleContent>
            <CTA
              direction="right"
              message={formatMessage({ id: "kiosk.forecast.cta" })}
              appearance="light"
              innerRef={el => this.introText.push(el)}
            />
          </div>
        </Intro>
        <ForecastPanel innerRef={el => (this.forecastPanel = el)}>
          <LocalForecast>
            <LocalHeadline innerRef={el => this.panelContents.push(el)}>
              <FormattedMessage id="kiosk.forecast.forecastTitle" />{" "}
              <LocationName>
                {data.loading ? "" : currentKiosk.friendlyName}
              </LocationName>
            </LocalHeadline>
            <LocalSubHeadline innerRef={el => this.panelContents.push(el)}>
              <FormattedMessage id="kiosk.forecast.forecastSubtitle" />
            </LocalSubHeadline>
            {this.renderLocalForecast()}
            <ButtonGroup innerRef={el => this.panelContents.push(el)}>
              <Button onClick={this.handleHistoryClick}>
                <FormattedMessage id="kiosk.forecast.buttons.history" />
              </Button>
              <Button onClick={this.handleLocationsClick}>
                <FormattedMessage id="kiosk.forecast.buttons.compare" />
              </Button>
            </ButtonGroup>
          </LocalForecast>
          {this.renderCAMMForecast()}
        </ForecastPanel>
      </BackgroundContainer>
    )
  }
}

const ViewWithData = compose(withCurrentKioskData, withCAMMForecast)(
  CurrentAirQualityView
)
export default injectIntl(ViewWithData)



// WEBPACK FOOTER //
// ./src/Kiosk/CurrentAirQualityView/CurrentAirQualityView.js